import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Box, useTheme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import CreateCommunityModal from '../components/community/CreateCommunityModal';
import CreatePostModal from '../components/community/CreatePostModal';
import CommunitySidebar from '../components/community/CommunitySidebar';
import LoginRequiredModal from '../components/community/LoginRequiredModal';
import CommunityMobileBar from '../components/community/CommunityMobileBar';
import { communityApi } from '../services/communityApi';
import { postApi } from '../services/postApi';
import { Community, Post } from '../types/community';

// Import new components
import CommunityHeader from '../components/community/CommunityHeader';
import SearchBar from '../components/community/SearchBar';
import CommunityList from '../components/community/CommunityList';
import PostList from '../components/community/PostList';
import ActionButtons from '../components/community/ActionButtons';

// Define interface for top comment
interface TopComment {
  id: string;
  content: string;
  author: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  createdAt: string;
  upvotesCount: number;
  downvotesCount: number;
}

const CommunityPage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const theme = useTheme();
  const navigate = useNavigate();
  const videoRefs = useRef<{[key: string]: HTMLVideoElement | null}>({});
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for mobile view
  const [mobileView, setMobileView] = useState<'posts' | 'communities' | 'stats'>('posts');

  const [communities, setCommunities] = useState<Community[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [postSortBy, setPostSortBy] = useState<'desc' | 'asc'>('desc');
  const [activeTab, setActiveTab] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false);
  const [userCommunities, setUserCommunities] = useState<Community[]>([]);
  const [videoMuted, setVideoMuted] = useState<{[key: string]: boolean}>({});
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  // New state for top comments
  const [topComments, setTopComments] = useState<{[postId: string]: TopComment}>({});

  // Scroll detection state
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  // Scroll detection effect
  useEffect(() => {
    const handleScroll = () => {
      // Show the bar
      setIsScrolling(true);
      
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Hide the bar after 5 seconds of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 5000);
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial setup - show bar if not at top
    if (window.scrollY > 0) {
      setIsScrolling(true);
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 5000);
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Function to fetch top comment for a post
  const fetchTopCommentForPost = useCallback(async (postId: string) => {
    try {
      const response = await postApi.getPostComments(postId, {
        page: 1,
        limit: 1
      });
      
      if (response.success && response.comments && response.comments.length > 0) {
        const comment = response.comments[0];
        setTopComments(prev => ({
          ...prev,
          [postId]: {
            id: comment.id,
            content: comment.content,
            author: comment.author,
            createdAt: comment.createdAt,
            upvotesCount: comment.upvotesCount,
            downvotesCount: comment.downvotesCount
          }
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch comments for post ${postId}:`, error);
    }
  }, []);

  // Function to fetch top comments for all posts
  const fetchTopCommentsForPosts = useCallback(async (postIds: string[]) => {
    // Fetch top comments for each post (limit to 5 at a time to avoid rate limiting)
    const batchSize = 5;
    for (let i = 0; i < postIds.length; i += batchSize) {
      const batch = postIds.slice(i, i + batchSize);
      const promises = batch.map(postId => fetchTopCommentForPost(postId));
      await Promise.all(promises);
      // Small delay between batches
      if (i + batchSize < postIds.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }, [fetchTopCommentForPost]);

  // Modified fetchAllPosts to include top comments fetching
  const fetchAllPosts = async () => {
  try {
    setPostsLoading(true);
    let allPosts: Post[] = [];
    
    // Use getPostsWithVotes instead of getPosts if available
    const response = await postApi.getPosts({
      sortBy: postSortBy === 'desc' ? 'newest' : 'oldest',
      page: 1,
      limit: 20
    });
    
    if (response.success && response.posts) {
      // Ensure each post has userVote field
      allPosts = response.posts.map(post => ({
        ...post,
        userVote: post.userVote || null
      }));
    }
    
    console.log('Fetched posts with votes:', allPosts.map(p => ({
      id: p.id,
      title: p.title,
      userVote: p.userVote
    })));
    
    setPosts(allPosts);
    
  } catch (error) {
    console.error('Failed to fetch posts:', error);
    toast.error('Failed to load posts');
    setPosts([]);
  } finally {
    setPostsLoading(false);
  }
};

  const fetchCommunities = async () => {
    try {
      setLoading(true);
      
      const response = await communityApi.getCommunities({
        search: searchQuery || undefined,
        sortBy: sortBy,
        page: 1,
        limit: 20
      });
      
      if (response.success) {
        const safeCommunities = (response.communities || []).map((community: Community) => ({
          ...community,
          membersCount: community.membersCount || 0,
          postsCount: community.postsCount || 0,
          owner: community.owner || { id: '', username: 'Unknown' },
          isMember: community.isMember || false,
          userRole: community.userRole || null,
          members: community.members || []
        }));
        
        if (activeTab === 0) setCommunities(safeCommunities);
        else if (activeTab === 1) setUserCommunities(safeCommunities);
      }
    } catch (error: any) {
      console.error('Failed to fetch communities:', error);
      toast.error('Failed to load communities');
      
      if (activeTab === 0) setCommunities([]);
      else if (activeTab === 1) setUserCommunities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCommunities = async () => {
    if (!user) return;
    
    try {
      const response = await communityApi.getUserCommunities({
        page: 1,
        limit: 20
      });
      
      if (response.success) {
        const safeCommunities = (response.communities || []).map((community: Community) => ({
          ...community,
          membersCount: community.membersCount || 0,
          postsCount: community.postsCount || 0,
          owner: community.owner || { id: '', username: 'Unknown' },
          isMember: true,
          userRole: 'member',
          members: community.members || []
        }));
        setUserCommunities(safeCommunities);
      }
    } catch (error) {
      console.error('Failed to fetch user communities:', error);
      toast.error('Failed to load your communities');
    }
  };

  useEffect(() => {
    fetchCommunities();
  }, [sortBy, activeTab, searchQuery]);

  useEffect(() => {
    fetchAllPosts();
  }, [postSortBy]);

  useEffect(() => {
    if (user && activeTab === 1) {
      fetchUserCommunities();
    }
  }, [user, activeTab]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== '') {
        fetchCommunities();
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleCreateCommunity = () => {
    if (!user) {
      setLoginModalOpen(true);
      return;
    }
    setCreateModalOpen(true);
  };

  const handleCreatePost = () => {
    if (!user) {
      setLoginModalOpen(true);
      return;
    }
    setCreatePostModalOpen(true);
  };

  const handleCommunityCreated = (newCommunity: Community) => {
    setCommunities([newCommunity, ...communities]);
    setCreateModalOpen(false);
    toast.success('Community created successfully!');
  };

  const handlePostCreated = (newPost: Post) => {
  setPosts([newPost, ...posts]);
  setCreatePostModalOpen(false);
  toast.success('Post created successfully!');
  // Refresh to get the latest posts with proper vote status
  fetchAllPosts();
};

  const handleVote = async (postId: string, vote: 'up' | 'down' | 'remove') => {
  if (!user) {
    setLoginModalOpen(true);
    return;
  }

  try {
    // Determine what vote to send based on current state
    const currentPost = posts.find(p => p.id === postId);
    if (!currentPost) return;

    let voteToSend = vote;
    
    // If clicking same vote type again, remove it
    if ((vote === 'up' && currentPost.userVote === 'up') || 
        (vote === 'down' && currentPost.userVote === 'down')) {
      voteToSend = 'remove';
    }
    
    // Send the vote to API
    const response = await postApi.votePost(postId, voteToSend);
    
    if (response.success) {
      // Update the post with the API response data
      setPosts(prevPosts => 
        prevPosts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              upvotes: response.post.upvotes,
              downvotes: response.post.downvotes,
              score: response.post.score,
              userVote: response.post.userVote // Use the userVote from API
            };
          }
          return post;
        })
      );
      toast.success(response.message || 'Vote updated');
    }
    
  } catch (error) {
    console.error('Vote error:', error);
    toast.error('Failed to vote on post');
    // Refetch to get correct state
    fetchAllPosts();
  }
};

const refreshPosts = () => {
  fetchAllPosts();
};

  const toggleVideoMute = (postId: string) => {
    const video = videoRefs.current[postId];
    if (video) {
      video.muted = !video.muted;
      setVideoMuted(prev => ({
        ...prev,
        [postId]: video.muted
      }));
    }
  };

  const handleVideoRef = (postId: string, el: HTMLVideoElement | null) => {
    videoRefs.current[postId] = el;
    if (el) {
      el.loop = true;
      el.autoplay = true;
      el.playsInline = true;
      el.muted = videoMuted[postId] || true;
    }
  };

  // Voice command for video mute
  useEffect(() => {
    const handleVoiceCommand = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'M') {
        e.preventDefault();
        // Toggle mute for all videos or focused video
        const postIds = Object.keys(videoRefs.current);
        if (postIds.length > 0) {
          const firstPostId = postIds[0];
          toggleVideoMute(firstPostId);
          toast.success(videoMuted[firstPostId] ? 'Video unmuted' : 'Video muted');
        }
      }
    };

    window.addEventListener('keydown', handleVoiceCommand);
    return () => window.removeEventListener('keydown', handleVoiceCommand);
  }, [videoMuted]);

  const handleBack = () => navigate(-1);

  const displayCommunities = activeTab === 0 ? communities : userCommunities;

  // Determine what to show based on mobile view
  const showCommunityList = !isMobile || mobileView === 'communities';
  const showPostList = !isMobile || mobileView === 'posts';
  const showSidebar = !isMobile || mobileView === 'stats';
  // Show SearchBar only in mobile when in communities tab
  const showSearchBar = !isMobile || mobileView === 'communities';

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      '& *::-webkit-scrollbar': { display: 'none' },
      '& *': { msOverflowStyle: 'none', scrollbarWidth: 'none' },
      pb: isMobile ? '100px' : 0, // Add padding for mobile bar
    }}>
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        
        {/* Only show CommunityHeader on desktop */}
        {!isMobile && (
          <CommunityHeader 
            handleBack={handleBack}
            isDark={isDark}
            goldColor={goldColor}
            glassEffect={{
              background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: isDark 
                ? '1px solid rgba(196, 182, 111, 0.2)' 
                : '1px solid rgba(5, 24, 20, 0.1)',
              boxShadow: isDark 
                ? '0 20px 60px rgba(0,0,0,0.7)' 
                : '0 20px 60px rgba(5, 24, 20, 0.1)',
            }}
            communities={communities} 
          />
        )}

        {/* Show SearchBar only when appropriate */}
        {showSearchBar && (
          <SearchBar 
            searchQuery={searchQuery}
            handleSearch={handleSearch}
            isDark={isDark}
            goldColor={goldColor}
            deepGreen={deepGreen}
            glassEffect={{
              background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: isDark 
                ? '1px solid rgba(196, 182, 111, 0.2)' 
                : '1px solid rgba(5, 24, 20, 0.1)',
              boxShadow: isDark 
                ? '0 20px 60px rgba(0,0,0,0.7)' 
                : '0 20px 60px rgba(5, 24, 20, 0.1)',
            }}
            gradientButtonStyle={{
              background: isDark 
                ? `linear-gradient(135deg, ${goldColor} 0%, rgba(196, 182, 111, 0.8) 100%)`
                : 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)',
              color: isDark ? deepGreen : goldColor,
              borderRadius: '12px',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: `0 4px 20px rgba(${isDark ? '196, 182, 111' : '5, 24, 20'}, 0.2)`,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 8px 30px rgba(${isDark ? '196, 182, 111' : '5, 24, 20'}, 0.3)`,
                background: isDark 
                  ? `linear-gradient(135deg, rgba(196, 182, 111, 0.9) 0%, rgba(196, 182, 111, 0.7) 100%)`
                  : 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)',
              }
            }}
          />
        )}

        <Grid container spacing={3}>
          {/* Left Column - Communities & Action Buttons (Hidden on mobile unless in communities view) */}
          {(showCommunityList || !isMobile) && (
            <Grid size={{ xs: 12, md: 4, lg: 3 }} sx={{ minWidth: 0 }}>
              {!isMobile && (
                <ActionButtons
                  handleCreatePost={handleCreatePost}
                  handleCreateCommunity={handleCreateCommunity}
                  isDark={isDark}
                  goldColor={goldColor}
                  deepGreen={deepGreen}
                  glassEffect={{
                    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: isDark 
                      ? '1px solid rgba(196, 182, 111, 0.2)' 
                      : '1px solid rgba(5, 24, 20, 0.1)',
                    boxShadow: isDark 
                      ? '0 20px 60px rgba(0,0,0,0.7)' 
                      : '0 20px 60px rgba(5, 24, 20, 0.1)',
                  }}
                />
              )}
              
              {showCommunityList && (
                <CommunityList
                  communities={displayCommunities}
                  loading={loading}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isDark={isDark}
                  goldColor={goldColor}
                  deepGreen={deepGreen}
                  navigate={navigate}
                  glassEffect={{
                    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: isDark 
                      ? '1px solid rgba(196, 182, 111, 0.2)' 
                      : '1px solid rgba(5, 24, 20, 0.1)',
                    boxShadow: isDark 
                      ? '0 20px 60px rgba(0,0,0,0.7)' 
                      : '0 20px 60px rgba(5, 24, 20, 0.1)',
                  }}
                  user={user}
                />
              )}
            </Grid>
          )}

          {/* Middle Column - Posts (Hidden on mobile unless in posts view) */}
          {(showPostList || !isMobile) && (
            <Grid size={{ xs: 12, md: 4, lg: 6 }} sx={{ minWidth: 0 }}>
              <PostList
                posts={posts}
                postsLoading={postsLoading}
                activeTab={activeTab}
                postSortBy={postSortBy}
                setPostSortBy={setPostSortBy}
                isDark={isDark}
                goldColor={goldColor}
                deepGreen={deepGreen}
                user={user}
                handleCreatePost={handleCreatePost}
                handleVote={handleVote}
                handleVideoRef={handleVideoRef}
                toggleVideoMute={toggleVideoMute}
                videoMuted={videoMuted}
                navigate={navigate}
                glassEffect={{
                  background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: isDark 
                    ? '1px solid rgba(196, 182, 111, 0.2)' 
                    : '1px solid rgba(5, 24, 20, 0.1)',
                  boxShadow: isDark 
                    ? '0 20px 60px rgba(0,0,0,0.7)' 
                    : '0 20px 60px rgba(5, 24, 20, 0.1)',
                }}
                gradientButtonStyle={{
                  background: isDark 
                    ? `linear-gradient(135deg, ${goldColor} 0%, rgba(196, 182, 111, 0.8) 100%)`
                    : 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)',
                  color: isDark ? deepGreen : goldColor,
                  borderRadius: '12px',
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: `0 4px 20px rgba(${isDark ? '196, 182, 111' : '5, 24, 20'}, 0.2)`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 8px 30px rgba(${isDark ? '196, 182, 111' : '5, 24, 20'}, 0.3)`,
                    background: isDark 
                      ? `linear-gradient(135deg, rgba(196, 182, 111, 0.9) 0%, rgba(196, 182, 111, 0.7) 100%)`
                      : 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)',
                  }
                }}
                // Pass top comments to PostList
                topComments={topComments}
                // Add refresh function
                refreshPosts={fetchAllPosts}
              />
            </Grid>
          )}

          {/* Right Column - Sidebar (Hidden on mobile unless in stats view) */}
          {(showSidebar || !isMobile) && (
            <Grid size={{ xs: 12, md: 4, lg: 3 }} sx={{ minWidth: 0 }}>
              <CommunitySidebar />
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Mobile Navigation Bar - Only show when scrolling */}
      {isMobile && isScrolling && (
        <CommunityMobileBar
          activeView={mobileView}
          setActiveView={setMobileView}
          isDark={isDark}
          goldColor={goldColor}
          deepGreen={deepGreen}
          handleCreatePost={handleCreatePost}
          handleCreateCommunity={handleCreateCommunity}
        />
      )}

      <CreateCommunityModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCommunityCreated}
      />

      <CreatePostModal
        open={createPostModalOpen}
        onClose={() => setCreatePostModalOpen(false)}
        onSuccess={handlePostCreated}
        userCommunities={userCommunities}
      />

      <LoginRequiredModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
      />
    </Box>
  );
};

export default CommunityPage;